import { ReactNode } from 'react';
import { ContentType, ImageInfoConfig } from '../constants/common';
import {
  EmailModuleAttributesType,
  EmailModuleOptionsContentType,
} from './EmailModules';
import {
  BannerSuperScriptsType,
  ContentEmailModulesSuperScriptType,
  EmailModulesSuperScriptsType,
  SocialSuperScriptsType,
} from './SuperScripts';
import { PregenerateData } from './userSessionContext';

export type BannerFrameType = {
  id: string;
  body: string;
};

export type ContentOptions = {
  frames: BannerFrameType[];
  optionId: string;
  call_to_action: string;
};

export type BannerFramesListType = {
  id: string;
  parentId: string;
  versionSequence: number;
  contentOptions: ContentOptions[];
  superScripts: BannerSuperScriptsType[];
  imagesRecommend?: ImageRecommendation[];
  contentReferences?: References[];
  score: {
    value: number;
  };
  requestParams?: unknown;
};

// Define the common parts of the response
export interface CommonContentResult {
  message: string;
  status: 200;
  statusText: string;
  success: boolean;
  data: {
    sessionId: string;
    action: string;
    channel: string;
    contentType: ContentType;
    status: string;
  };
}

// Define the response structure with generated content
export interface ContentGeneratedResult extends CommonContentResult {
  data: {
    isInclusionsExclusionsInContentLang: boolean;
    generatedContent: GeneratedContentData[];
  } & Omit<CommonContentResult['data'], 'generatedContent'>;
  requestParams?: unknown;
}

// Define the response structure with translated content
export interface ContentTranslatedResult extends CommonContentResult {
  data: {
    isInclusionsExclusionsInContentLang: boolean;
    translatedContent: GeneratedContentData[];
  } & Omit<CommonContentResult['data'], 'translatedContent'>;
}

export interface ImagesResult {
  message: '';
  status: 200;
  statusText: '';
  success: true;
  data: {
    id: string;
    channel: string;
    content: [
      {
        optionId: string;
        // for social media only
        recommendedImages?: string[];
        imagesReferenceFrame: string;
        frames: [
          {
            id: string;
            recommendedImages: [
              {
                imageName: string;
              },
            ];
          },
        ];
      },
    ];
  };
}

// Interface for recommended images
export interface RecommendedImage {
  imageId: string;
  imageName: string;
  caption: string;
  category?: string;
  imageType?: 'CHART' | 'GRAPH' | 'TABLE' | 'IMAGE';
  source?: ImageSource;
  imageMetadata?: ImageMetadata;
}

// Interface for frames containing recommended images
export interface Frame {
  id: string;
  recommendedImages: RecommendedImage[];
}

// Interface for image recommendations
export interface ImageRecommendation {
  optionId: string;
  imagesReferenceFrame?: string; // Optional, as not all objects have this
  frames?: Frame[]; // Optional, as not all objects have this
  recommendedImages?: RecommendedImage[]; // Optional, as not all objects have this
}

// Interface for the main data structure
export interface ImageData {
  id: string;
  sessionId: string;
  action: string;
  channel: string;
  imagesRecommend: ImageRecommendation[];
}

export interface ImageApiResponse {
  success: boolean;
  message: string;
  errors: string[];
  data: ImageData;
}

export interface CannedParametersResult {
  message?: '';
  status?: 200;
  statusText?: '';
  success?: true;
  data: CannedPramsResponseType;
}

export type CannedParams = {
  id: string;
  label: string;
  name?: string;
  recommend?: boolean;
  category?: BrandCategory;
};

export type CannedLanguage = {
  id: string;
  label: string;
  name?: string;
  isPrimary?: boolean;
  isFrequentlyUsed?: boolean;
};

export type CannedPramsResponseType = {
  items: CannedParams[];
  extendedLibraryItems?: CannedParams[];
};

export interface ApiError {
  success: boolean;
  message: string;
  errors?: string[];
  data: unknown;
  status: number;
}

export interface FeedbackResult {
  success: boolean;
  message: string;
  errors?: string[];
  data: unknown;
  status: number;
}

export interface TrackContentCopyResult {
  success: true;
  message: string;
  errors: string[];
  data: unknown;
}

export type SegmentType = {
  subHeader: string;
  texts: string[];
};

export type SectionsType = {
  name: string;
  body: string | SegmentType[];
};

export enum ActionNames {
  COPY = 'COPY',
  COPY_TEXT = 'COPY_TEXT',
  REGENERATE = 'REGENERATE',
}

export enum ActionLevel {
  SEGMENT = 'segment',
  SECTION = 'section',
}

export interface Action {
  name: string;
  label: string;
  enabled: boolean;
  icon?: React.ReactNode | React.JSX.Element | string;
}

export interface ContentsDataType {
  title: string;
  actionLevel?: ActionLevel;
  sections: SectionsType[];
}

export enum AssetType {
  IMAGE = 'IMAGE',
  CHART_OR_GRAPH = 'CHART_OR_GRAPH',
}

export enum ImageSource {
  AEM = 'AEM',
  SYSTEM = 'SYSTEM',
  EXT_LIB = 'EXT_LIB',
}

export interface ImageMetadata {
  documentId?: string;
  documentTitle?: string;
  uploadUserId?: string;
  citation?: string;
}

export interface ContentTypeImage {
  name: string;
  altText?: string;
  frameId: string;
  id: string;
  caption?: string;
  translatedCaption?: string;
  page?: string;
  category?: string;
  assetType?: AssetType;
  source?: ImageSource;
  imageMetadata?: ImageMetadata;
}

export interface BannerContentType {
  title: string;
  frames: BannerFrameType[];
  optionId: string;
  isLoading: boolean;
}

export interface CoreMessageSectionItem {
  id: string;
  code: string;
  name: string;
  sequence: number;
  content: string;
}

export interface EmailContentSection {
  path: string;
  name: string;
  title: string;
  markdownText: string;
  isLoading?: boolean;
}
export interface EmailContent {
  optionId: string;
  topSection: {
    title: string;
    preHeader: EmailContentSection;
    subjectLineOptions: EmailContentSection;
    previewTextOptions: EmailContentSection;
    isLoading?: boolean;
  };
  emailBody: {
    title: string;
    introCopy: EmailContentSection;
    callToActionsOption: EmailContentSection;
  };
  isLoading?: boolean;
}

export type SocialContentForm = {
  name: string;
  title: string;
  isLoading: boolean;
  headline: {
    name: string;
    title: string;
    text: string;
  };
  body: {
    name: string;
    title: string;
    text: string;
  };
  hashtags: {
    name: string;
    title: string;
    text: string;
  };
};

export type SocialContent = {
  title: string;
  optionId: string;
  isLoading: boolean;
  shortForm: SocialContentForm;
  longForm: SocialContentForm;
};

export type CoreMessageContentType = {
  id: string;
  name: string;
  code: string;
  sequence: number;
  content: string;
};

export type CoreMessageContent = Array<CoreMessageContentType>;

// Base interface for all references
export interface BaseReference {
  id: string;
  description: string;
  referenceLink: string;
  category?: string;
  country?: string;
  sequence?: number;
}

// Claim-specific reference type
export interface ClaimReference extends BaseReference {
  type: 'CLAIM';
  code: string;
  documentNames: string[];
  status: string;
  isSubstitute: boolean;
}

// Quote-specific reference type
export interface QuoteReference extends BaseReference {
  type: 'QUOTE';
  docId: string;
  docTitle: string;
  uploadUserId: string;
  citation: string;
}

// Union type for all reference types
export type References = ClaimReference | QuoteReference;

export interface ReferencesResponseData {
  id: string;
  channel: string;
  contentReferences: References[]; // updated key from claimReferences as per swagger
}

export interface ReferencesResult {
  success: boolean;
  message: string;
  errors?: string[];
  data: ReferencesResponseData | null;
  status: number;
}

export interface ReferencesResponse {
  success: boolean;
  data?: ReferencesResponseData | [];
}

export interface AnalyticsData {
  brand: string;
  contentType: string;
}

export type ImageRecomendationInfoType = {
  type: ImageInfoConfig;
  labelText: string;
  ctaText: string;
};

export type GetGeneratedContentParams = {
  sessionId: string;
  contentSessionId: string;
  contentGroupId: string;
  contentId: string;
  action: string;
  channel?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
};

export type GetTranslatedContentParams = {
  sessionId: string;
  action: string;
  contentId: string;
  contentGroupId?: string;
  channel?: string;
  page?: number;
  pageSize?: number;
  languages?: string;
  language?: string;
  sort?: string;
  allTranslations?: boolean;
};

type Score = {
  value: number;
};

type BannerImageNames = {
  imageName: string;
};

type BannerRecommendedImagesByFrames = {
  recommendedImages: BannerImageNames[];
};

export type BannerRecommendedImages = {
  optionId: string;
  imagesReferenceFrame: string;
  frames: BannerRecommendedImagesByFrames[];
};

export type RecommendedImages = {
  optionId: string;
  recommendedImages: string[];
};

export type ImagesRecommended = {
  id: string;
  parentId: string;
  versionSequence: number;
  contentImages: RecommendedImages[];
};

export type GeneratedSocialContent = {
  optionId: string;
  shortForm: {
    headline: string;
    body: string;
    hashtags: string[];
  };
  longForm: {
    headline: string;
    body: string;
    hashtags: string[];
  };
};

export type GeneratedEmailModuleContentDataType = {
  id: string;
  parentId: string;
  name: string;
  versionSequence: number;
  score: Score;
  primaryMessage?: string;
  attributes: EmailModuleAttributesType[];
  contentOptions: EmailModuleOptionsContentType[];
  superScripts?:
    | EmailModulesSuperScriptsType[]
    | ContentEmailModulesSuperScriptType[];
  contentReferences?: References[];
  imagesRecommend?: ImageRecommendation[];
  langCode?: string;
  langLabel?: string;
};

export type GeneratedBannerContentDataType = BannerFramesListType;

export interface GeneratedSocialContentDataType {
  id: string;
  parentId: string;
  versionSequence: number;
  contentOptions: GeneratedSocialContent[];
  superScripts?: SocialSuperScriptsType[];
  imagesRecommend?: ImageRecommendation[];
  contentReferences: References[];
  score: Score;
  requestParams?: unknown;
}

export interface GeneratedClaimsDataType {
  id: string;
  parentId: string;
  versionSequence: number;
  language: string;
  operationType: string;
  status: string;
  claimsContent: ClaimsType;
  pagination: PaginationType;
}

export type GeneratedContentData =
  | GeneratedEmailModuleContentDataType
  | GeneratedBannerContentDataType
  | GeneratedSocialContentDataType;

/** Generated-Content API response structure */
export interface GeneratedContentResultData {
  sessionId: string;
  action: string;
  channel: string;
  primaryMessage?: string;
  generatedContent: GeneratedContentData[];
  translatedContent: GeneratedContentData[];
}

export interface GeneratedContentResult {
  success: boolean;
  message?: string;
  errors?: string[];
  data?: GeneratedContentResultData;
}

export interface TranslatedContentResultData {
  sessionId: string;
  action: string;
  channel: string;
  contentGroupId?: string;
  primaryMessage?: string;
  translatedContent: GeneratedClaimsDataType[];
}

export interface TranslatedContentResult {
  success: boolean;
  message?: string;
  errors?: string[];
  data?: TranslatedContentResultData;
}

/** Generate API response structure */
export interface GenerateResultData {
  sessionId: string;
  action: string;
  channel: string;
  generatedContent: GeneratedContentData[];
}

export interface GenerateResult {
  success: boolean;
  message?: string;
  errors?: string[];
  data?: GenerateResultData;
}

export interface TranslatedContent extends GeneratedEmailModuleContentDataType {
  langCode?: string;
  langLabel?: string;
}

export interface NotificationsResponseData {
  id: string;
  type: 'SERVICE_OUTAGE' | string; // Extend with more types if needed
  description: string;
  startDate: string;
  endDate: string;
  category: string; // SCHEDULED | AUTOMATED
  enabled: boolean;
  impactedFeatures?: string[];
  tooltip?: { label: string; content: ReactNode };
}

export interface NotificationsResult {
  success: boolean;
  message?: string;
  errors?: string[];
  data: { notifications: NotificationsResponseData[] } | null;
  status?: number;
}

export type Citation = {
  text: string;
  location: string;
};

export type ClaimType = {
  citation: Citation[];
  claimId: string;
  claimText: string;
  primaryMessages: string[];
  relevanceScore: number;
};

export type CompareClaimType = {
  citation: Citation[];
  claimId: string;
  claimText: string;
  translatedClaimText: string;
  primaryMessages: string;
  relevanceScore: number;
};

export type ClaimsType = {
  claims: ClaimType[];
};

export type PaginationType = {
  nextPage: number;
  page: number;
  previousPage: number;
  total: number;
  totalPages: number;
};

export type ClaimsContentType = {
  claimsContent: ClaimsType & {
    pagination: PaginationType;
  };
};

export type GeneratedClaimsType = {
  id: string;
  score: {
    value: number;
  };
  parentId: string;
  versionSequence: number;
  operationType: string;
  claimsContent: {
    pagination: PaginationType;
    claims: ClaimType[];
  };
};

export type ModuleComponentType = {
  id: string;
  name: string;
  code: string;
  sequence: number;
};

export type ModuleComponentsPayload = {
  components: Array<ModuleComponentType>;
  generateComponents: string[];
  deleteComponents: string[];
};

export type SessionContextPayload = {
  sessionId: string;
  sessionContext?: Array<{
    operation?: string;
    contextType?: string;
    contentSessionId?: string;
    action?: string;
    pregenerateData: PregenerateData;
    contextData?: Array<{
      contentGroupId?: string;
      contentVersions?: Array<{
        contentId: string;
        versionSequence: number;
        operationType: string;
        language: string;
        translatedContentDetails: Array<{
          translatedLanguage?: string;
          translatedSequence?: number;
        }>;
        isLatestContent: boolean;
      }>;
    }>;
  }>;
};

export enum ContextType {
  GENERATION_CONTEXT = 'GENERATION_CONTEXT',
  EMAIL_MODULE_CONTEXT = 'EMAIL_MODULE_CONTEXT',
  CORE_MESSAGE_CONTEXT = 'CORE_MESSAGE_CONTEXT',
}

export enum OperationType {
  GENERATE = 'GENERATE',
  REFINE = 'REFINE',
  REGENERATE = 'REGENERATE',
}

export enum BrandCategory {
  ONBOARDED = 'ONBOARDED',
  NON_ONBOARDED = 'NON_ONBOARDED',
}

/** Update Generated Content API types */
export interface UpdateGeneratedContentRequestData {
  sessionId: string;
  contentSessionId: string;
  contentGroupId: string;
  action: string;
  channel: string;
  generatedContentUpdate: GeneratedContentUpdateItem[];
}

export interface UpdateGeneratedContentRequest {
  data: UpdateGeneratedContentRequestData;
}

export interface GeneratedContentUpdateItem {
  id: string;
  contentOptions?: Array<{
    optionId: string;
    frames?: Array<{
      id: string;
      body: string;
    }>;
    callToAction?: string;
    header?: string;
    preHeader?: string;
    body?: string;
    closingBody?: string;
    clickToAction?: string;
    subheader?: string;
    bulletedCopy?: string[];
    subjectLine?: string;
    previewText?: string;
    shortForm?: {
      headline: string;
      body: string;
      hashtags: string[];
    };
    longForm?: {
      headline: string;
      body: string;
      hashtags: string[];
    };
  }>;
  contentSummary?: {
    summaries: Array<{
      id: string;
      summaryText: string;
    }>;
  };
  claimsContent?: {
    claims: Array<{
      claimId: string;
      claimText: string;
    }>;
  };
  updateComponents?: Array<{
    id: string;
    updateComponentData: {
      header?: string;
      subheader?: string;
      message?: string;
      callToAction?: string;
    };
  }>;
}

export interface UpdateGeneratedContentResponseData {
  sessionId: string;
  contentSessionId: string;
  contentGroupId: string;
  action: string;
  channel: string;
  generatedContent: GeneratedContentData[];
}

export interface UpdateGeneratedContentResponse {
  success: boolean;
  message?: string;
  errors?: string[];
  data?: UpdateGeneratedContentResponseData;
}
